

import { useReducer, useEffect } from "react"
import ErrorBoundary from "../../../components/ErrorBoundary"
import { Box } from "@mui/material"
import DefaultModal from "../../../components/modal/DefaultModal/DefaultModal"
import lockedReducer from "../../../lib/reducer"
import { emitSnackbar } from "../../../lib/snackbar-emitter"
import api, { getEnterpriseId } from "../../../lib/api"
import QRCode from "react-qr-code";
import { loader } from "../../../lib/loader"

let pollingId = 0

const getPairingCodeFromResponse = (data = {}) => (
    data.pairingCode ||
    data.PairingCode ||
    data.qrcode?.pairingCode ||
    data.qrCode?.pairingCode ||
    ''
)

const getQrCodeFromResponse = (data = {}) => (
    data.code ||
    data.Code ||
    data.qrcode?.code ||
    data.qrCode?.code ||
    ''
)

const formatPairingCode = (code = '') => (
    String(code)
        .replace(/[\s-]/g, '')
        .replace(/(.{4})(?=.)/g, '$1-')
)


const getQR = async (account, setState, { showLoader = true } = {}) => {
    const enterpriseId = getEnterpriseId()
    if (showLoader) {
        loader.on()
    }
    try {
        const res = await api.get(
            `/enterprises/${enterpriseId}/scout_accounts/${account.id}/getqr/`,
            { cache: 'no-store' }
        );
        const qrcode = getQrCodeFromResponse(res.data)
        const pairingCode = getPairingCodeFromResponse(res.data)

        if (res.status === 200) {
            setState((current) => ({
                qrcode: qrcode || current.qrcode,
                pairingCode: pairingCode || current.pairingCode,
                pairingError: pairingCode || current.pairingCode
                    ? ''
                    : 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.',
            }));
        } else if (showLoader) {
            console.warn('[Scout pairing] getqr returned non-200 status', res)
            setState({ pairingError: 'Não foi possível gerar o código de pareamento.' })
            emitSnackbar({ message: 'Erro ao gerar o código QR.', severity: 'error' })
        }
    } catch (error) {
        if (showLoader) {
            console.error("Error generating QR/pairing code", error);
            setState({ pairingError: 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.' })
            emitSnackbar({ message: 'Erro ao gerar os códigos de conexão.', severity: 'error' })
        }
    } finally {
        if (showLoader) {
            loader.off()
        }
    }
};

const checkConnection = async (account, handleClose, handleSuccess) => {
    const enterpriseId = getEnterpriseId()
    const res = await api.get(
        `/enterprises/${enterpriseId}/scout_accounts/${account.id}/checkconnection/`
    )
    if (res.status === 200) {
        if (res.data.state === "open") {
            clearInterval(pollingId);
            emitSnackbar({ message: 'Número conectado com sucesso!', severity: 'success' })
            handleClose()
            handleSuccess();
            return true
        } else if (res.data.state === "wrongNumber") {
            clearInterval(pollingId);
            emitSnackbar({ message: 'Número errado para conectar.', severity: 'error' })
            handleClose();
            return true
        }
        return false
    } else {
        console.error("Error checking connection", res);
        emitSnackbar({ message: 'Conta não encontrada.', severity: 'error' })
        handleClose();
        return true
    }
};

const ConnectPhone = ({ account, handleClose, handleSuccess }) => {

    const [state, setState] = useReducer(lockedReducer, {
        qrcode: '',
        pairingCode: '',
        pairingError: '',
    })

    const formattedPairingCode = formatPairingCode(state.pairingCode)

    const copyPairingCode = async () => {
        try {
            await navigator.clipboard.writeText(String(state.pairingCode).replace(/[\s-]/g, ''))
            emitSnackbar({ message: 'Código de pareamento copiado.', severity: 'success' })
        } catch (error) {
            console.error("Error copying pairing code", error);
            emitSnackbar({ message: 'Não foi possível copiar o código.', severity: 'error' })
        }
    }

    useEffect(() => {
        getQR(account, setState);
        clearInterval(pollingId);
        pollingId = setInterval(async () => {
            const connectionResolved = await checkConnection(account, handleClose, handleSuccess);
            if (!connectionResolved) {
                await getQR(account, setState, { showLoader: false });
            }
        }, 5 * 1000);

        return () => {
            // Cleanup on component unmount
            clearInterval(pollingId);
        };
    }, [account, handleClose, handleSuccess]);

    return (
        <DefaultModal
            title={
                'Conectar telefone'
            }
            handleClose={handleClose}
            maxWidth="md"
        >
            <Box display='flex' gap='1rem' justifyContent='center' alignItems='flex-start' flexWrap='wrap' width='100%'>
                <Box sx={{ flex: '1 1 260px', maxWidth: 360 }}>
                    <div className="max-w-sm p-4 bg-white text-gray-900">
                        <p className="text-xs text-gray-600 mb-3">
                            Para conectar um telefone, siga os passos abaixo:
                        </p>
                        <ol className="space-y-2 list-none p-0 m-0">
                            {[
                                <>No celular a ser monitorado, abra o <strong>WhatsApp</strong></>,
                                <>Vá em <strong>"Menu"</strong> ou <strong>"Configurações"</strong></>,
                                <>Escolha <strong>"Dispositivos conectados"</strong></>,
                                <>Clique em <strong>"Conectar dispositivo"</strong></>,
                                <>Escaneie o código QR ao lado</>,
                            ].map((step, i) => (
                                <li key={i} className="flex gap-3 text-xs text-gray-800">
                                    <span className="font-bold text-gray-900 min-w-[20px]">{i + 1}.</span>
                                    <span>{step}</span>
                                </li>
                            ))}
                        </ol>
                    </div>
                </Box>
                <Box display='flex' flexDirection='column' gap='0.75rem' alignItems='center' sx={{ flex: '1 1 260px', maxWidth: 396, width: '100%' }}>
                    {state.qrcode &&
                        <Box className='bg-gray-100 rounded-xl p-4 text-center' sx={{ background: '#ebf4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                            <QRCode size='150' value={state.qrcode} />
                        </Box>}
                    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center min-w-[220px] w-full">
                        <p className="text-xs font-semibold text-gray-900 mb-1">
                            Código de pareamento
                        </p>
                        {state.pairingCode ? (
                            <>
                                <p className="font-mono text-2xl font-bold tracking-widest text-gray-900 mb-3">
                                    {formattedPairingCode}
                                </p>
                                <div className="flex gap-2 justify-center">
                                    <button
                                        type="button"
                                        className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white"
                                        onClick={copyPairingCode}
                                    >
                                        Copiar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-xs text-gray-600 mb-3">
                                    Use esta opção se preferir conectar pelo número em vez do QR Code.
                                </p>
                                {state.pairingError &&
                                    <p className="text-xs text-red-600 mb-3">
                                        {state.pairingError}
                                    </p>}
                            </>
                        )}
                    </div>
                    {state.pairingCode &&
                        <p className="max-w-[240px] text-xs text-gray-600 text-center">
                            No WhatsApp, escolha conectar com número de telefone e informe este código.
                        </p>}
                </Box>
            </Box>
        </DefaultModal>
    )
}

const ConnectPhoneWithBoundary = (props) => (
    <ErrorBoundary
        componentName="ConnectPhone"
        showReload
        logToBackend
        title="Erro ao conectar telefone"
        message="Ocorreu um erro no processo de conexão do telefone. Tente novamente."
    >
        <ConnectPhone {...props} />
    </ErrorBoundary>
)

export default ConnectPhoneWithBoundary
